/**
 * @fileoverview Gateway Change Context Provider
 *
 * This file provides React Context for tracking gateway data changes
 * within the ProjectManagement panel. It enables refreshing the AG Grid
 * list only when the panel is closed AND changes have occurred.
 *
 * Purpose:
 * - Track when gateway task/milestone dates are updated
 * - Signal to refresh AG Grid on panel close with state preservation
 * - Persist change tracking across tab switches within ViewPanelBody
 */

import React, { createContext, useContext, useState, useCallback } from "react";

/**
 * Gateway change context interface defining available state and operations
 */
interface GatewayChangeContextType {
  /** Whether any gateway data has been modified since panel opened */
  hasChanges: boolean;
  /** Mark that changes have been made (call after successful API update) */
  setHasChanges: (value: boolean) => void;
  /** Reset change tracking (call after refresh is triggered) */
  resetHasChanges: () => void;
}

const GatewayChangeContext = createContext<GatewayChangeContextType | null>(null);

/**
 * GatewayChangeProvider Component
 *
 * Provides gateway change tracking state to child components through React Context.
 * Should be placed at ViewPanelBody level to persist across tab switches.
 *
 * @param children - Child components that will have access to gateway change context
 */
export const GatewayChangeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasChanges, setHasChangesState] = useState(false);

  const setHasChanges = useCallback((value: boolean) => {
    setHasChangesState(value);
  }, []);

  const resetHasChanges = useCallback(() => {
    setHasChangesState(false);
  }, []);

  const value: GatewayChangeContextType = {
    hasChanges,
    setHasChanges,
    resetHasChanges,
  };

  return <GatewayChangeContext.Provider value={value}>{children}</GatewayChangeContext.Provider>;
};

/**
 * Hook to access gateway change context
 *
 * @returns Gateway change context with hasChanges state and operations
 * @throws Error if used outside of GatewayChangeProvider
 *
 * @example
 * ```tsx
 * const { hasChanges, setHasChanges } = useGatewayChange();
 *
 * // After successful date update:
 * setHasChanges(true);
 * ```
 */
export const useGatewayChange = (): GatewayChangeContextType => {
  const context = useContext(GatewayChangeContext);
  if (!context) {
    throw new Error("useGatewayChange must be used within a GatewayChangeProvider");
  }
  return context;
};

/**
 * Hook to optionally access gateway change context
 * Returns null if not within a GatewayChangeProvider (safe to use anywhere)
 *
 * @returns Gateway change context or null if not available
 *
 * @example
 * ```tsx
 * const gatewayChange = useOptionalGatewayChange();
 *
 * // Safely mark changes if context is available:
 * gatewayChange?.setHasChanges(true);
 * ```
 */
export const useOptionalGatewayChange = (): GatewayChangeContextType | null => {
  return useContext(GatewayChangeContext);
};

export default GatewayChangeContext;
